import { PostImpressionThreshholds } from '@/utils/behavior/applicationEvents/gtm-data-types';
const contextualizedLogMessage = (message) =>
  `elementImpressionObserver - ${message}`;

/**
 * Mixin for observing the on screen visibility of a given element
 * @param {Object} options - The options for the observer
 * @param {number} options.visibilityThreshold - Percentage (0-1) of element that must be visible to trigger the callback
 * @param {number} options.visibilityTimerThreshold - Duration (ms) element must remain visible before callback triggers
 * @param {string} options.targetRef - Required. Vue ref name of the element to observe
 * @param {Function} options.onImpression - Required callback function when visibility conditions are met
 * @returns {Object}
 * @usage: In component mounted hook, call the setup method with the desired ref and callback;
 * Observer cleanup is handled by the mixin
 * this.setupIntersectionObserver({
      targetRef: 'postElement',
      onImpression: () => {
        if (this.post) {
          eventManager.raisePostImpressionEvent({
            post_id: this.post.postId,
            post_author: this.post.author?.profileName || '',
            post_author_id: this.post.author?.profileId || '',
          });
        }
      },
    });
 */
export const elementImpressionObserver = {
  data() {
    return {
      elementImpressionObserver: {
        observer: null,
        hasBeenSeen: false,
        visibilityTimer: null,
        isCurrentlyVisible: false,
      },
    };
  },

  beforeDestroy() {
    this.cleanupIntersectionObserver();
  },

  methods: {
    setupIntersectionObserver(options = {}) {
      const {
        // We could use default values that aren't related to any specific impressions
        // If we use that mixin for something other than Posts, please consider updating that part
        visibilityThreshold = PostImpressionThreshholds.VisibilityThreshold,
        visibilityTimerThreshold = PostImpressionThreshholds.VisibilityTimerDuration,
        targetRef,
        onImpression,
      } = options;

      if (!onImpression) {
        throw new Error(
          contextualizedLogMessage('onImpression callback is required')
        );
      }

      if (!targetRef) {
        throw new Error(contextualizedLogMessage('targetRef is required'));
      }

      this.elementImpressionObserver.observer = new IntersectionObserver(
        ([entry]) => {
          if (this.elementImpressionObserver.hasBeenSeen) {
            // This case shouldn't happen, but we'll clean up the observer just in case
            this.cleanupIntersectionObserver();
            return;
          }

          const isVisible = entry.intersectionRatio >= visibilityThreshold;
          this.elementImpressionObserver.isCurrentlyVisible = isVisible;

          if (isVisible && !this.elementImpressionObserver.visibilityTimer) {
            this.elementImpressionObserver.visibilityTimer = setTimeout(() => {
              if (
                this.elementImpressionObserver.isCurrentlyVisible &&
                !this.elementImpressionObserver.hasBeenSeen
              ) {
                this.elementImpressionObserver.hasBeenSeen = true;
                onImpression();
                this.cleanupIntersectionObserver();
              }
            }, visibilityTimerThreshold);
          }

          if (!isVisible && this.elementImpressionObserver.visibilityTimer) {
            clearTimeout(this.elementImpressionObserver.visibilityTimer);
            this.elementImpressionObserver.visibilityTimer = null;
          }
        },
        { threshold: [visibilityThreshold] }
      );

      this.$nextTick(() => {
        const target = this.$refs[targetRef];
        if (!target) {
          // TODO from Nikola: this._isDestroyed is a private Vue 2 internal, it won't exist under Vue 3 (Nuxt 3),
          // so this check will need to be replaced when we migrate.
          if (this._isDestroyed) return;
          throw new Error(
            contextualizedLogMessage(
              `Target element with ref "${targetRef}" not found`
            )
          );
        }
        this.elementImpressionObserver.observer.observe(target);
      });
    },

    cleanupIntersectionObserver() {
      this.elementImpressionObserver.observer?.disconnect();
      this.elementImpressionObserver.observer = null;

      if (this.elementImpressionObserver.visibilityTimer) {
        clearTimeout(this.elementImpressionObserver.visibilityTimer);
        this.elementImpressionObserver.visibilityTimer = null;
      }
    },
  },
};
